import {
  FileText,
  Sparkles,
  TrendingUp,
  PieChart,
  BarChart3,
  Table2,
  Briefcase,
  Award,
  ArrowUpDown,
  DollarSign,
  List,
  Type,
  Globe,
  Shield,
  Minus,
  Heading,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type BlockCategory =
  | "cover"
  | "narrative"
  | "portfolio"
  | "holdings"
  | "activity"
  | "market"
  | "other";

export interface BlockDefinition {
  name: string;
  category: BlockCategory;
  icon: LucideIcon;
  description: string;
  defaultConfig: Record<string, unknown>;
  maxInstances: number;
}

export type BlockType = keyof typeof REPORT_BLOCKS;

export interface ReportBlock {
  id: string;
  type: BlockType;
  config: Record<string, unknown>;
}

export interface ReportSettings {
  periodStart: string;
  periodEnd: string;
  watermark?: string;
  headerText?: string;
  footerText?: string;
  showPageNumbers?: boolean;
  showDisclosures?: boolean;
  paperSize?: "a4" | "letter";
  orientation?: "portrait" | "landscape";
}

export const DEFAULT_SETTINGS: ReportSettings = {
  periodStart: "",
  periodEnd: "",
  watermark: "",
  headerText: "Portfolio Report",
  footerText: "Prepared by {{org_name}}",
  showPageNumbers: true,
  showDisclosures: true,
  paperSize: "a4",
  orientation: "portrait",
};

export const REPORT_BLOCKS = {
  cover_page: {
    name: "Cover Page",
    category: "cover" as const,
    icon: FileText,
    description: "Report title, client name, period, firm logo",
    defaultConfig: {},
    maxInstances: 1,
  },
  executive_summary: {
    name: "Executive Summary",
    category: "narrative" as const,
    icon: Sparkles,
    description: "AI-generated performance narrative",
    defaultConfig: { style: "ai_generated", paragraphs: 5 },
    maxInstances: 1,
  },
  portfolio_overview: {
    name: "Portfolio Overview",
    category: "portfolio" as const,
    icon: TrendingUp,
    description: "Total NAV, return, key metrics summary",
    defaultConfig: { showBenchmark: false, showPriorPeriod: true },
    maxInstances: 1,
  },
  asset_allocation: {
    name: "Asset Allocation",
    category: "portfolio" as const,
    icon: PieChart,
    description: "Allocation breakdown by asset class",
    defaultConfig: { chartType: "pie", showTable: true },
    maxInstances: 1,
  },
  performance_attribution: {
    name: "Performance Attribution",
    category: "portfolio" as const,
    icon: BarChart3,
    description: "Returns by asset class, top/bottom contributors",
    defaultConfig: { showContributors: true, topN: 5 },
    maxInstances: 1,
  },
  holdings_table: {
    name: "Holdings Table",
    category: "holdings" as const,
    icon: Table2,
    description: "Position details with market value and gain/loss",
    defaultConfig: { assetClasses: ["all"], sortBy: "market_value", showCost: true },
    maxInstances: 3,
  },
  alternatives_detail: {
    name: "Alternatives Detail",
    category: "holdings" as const,
    icon: Briefcase,
    description: "Fund-by-fund detail for PE/VC/RE positions",
    defaultConfig: { showCommitment: true, showDistributions: true },
    maxInstances: 1,
  },
  top_holdings: {
    name: "Top Holdings",
    category: "holdings" as const,
    icon: Award,
    description: "Largest positions by market value",
    defaultConfig: { topN: 10 },
    maxInstances: 1,
  },
  capital_activity: {
    name: "Capital Activity",
    category: "activity" as const,
    icon: ArrowUpDown,
    description: "Capital calls, distributions, net cash flow",
    defaultConfig: {},
    maxInstances: 1,
  },
  fee_summary: {
    name: "Fee Summary",
    category: "activity" as const,
    icon: DollarSign,
    description: "Management fees, performance fees, expense ratio",
    defaultConfig: { showBreakdown: true },
    maxInstances: 1,
  },
  transaction_log: {
    name: "Transaction Log",
    category: "activity" as const,
    icon: List,
    description: "Period transactions with dates and amounts",
    defaultConfig: { limit: 50 },
    maxInstances: 1,
  },
  custom_text: {
    name: "Custom Text",
    category: "narrative" as const,
    icon: Type,
    description: "Free-form text block",
    defaultConfig: { content: "" },
    maxInstances: 10,
  },
  market_commentary: {
    name: "Market Commentary",
    category: "market" as const,
    icon: Globe,
    description: "AI-generated macro market overview",
    defaultConfig: {},
    maxInstances: 1,
  },
  disclosures: {
    name: "Disclosures",
    category: "other" as const,
    icon: Shield,
    description: "Standard legal disclosures and methodology notes",
    defaultConfig: {},
    maxInstances: 1,
  },
  page_break: {
    name: "Page Break",
    category: "other" as const,
    icon: Minus,
    description: "Force a new page in the PDF",
    defaultConfig: {},
    maxInstances: 20,
  },
  section_header: {
    name: "Section Header",
    category: "other" as const,
    icon: Heading,
    description: "Custom section title with optional divider",
    defaultConfig: { title: "", showDivider: true },
    maxInstances: 20,
  },
} as const satisfies Record<string, BlockDefinition>;

export const BLOCK_CATEGORIES: { key: BlockCategory; label: string }[] = [
  { key: "cover", label: "Cover" },
  { key: "narrative", label: "Narrative" },
  { key: "portfolio", label: "Portfolio" },
  { key: "holdings", label: "Holdings" },
  { key: "activity", label: "Activity" },
  { key: "market", label: "Market" },
  { key: "other", label: "Other" },
];

export const PRESET_TEMPLATES: { name: string; description: string; blocks: BlockType[] }[] = [
  {
    name: "Quarterly Client Review",
    description: "Standard quarterly portfolio report for clients",
    blocks: [
      "cover_page",
      "executive_summary",
      "portfolio_overview",
      "asset_allocation",
      "holdings_table",
      "capital_activity",
      "fee_summary",
      "disclosures",
    ],
  },
  {
    name: "Annual Performance Report",
    description: "Comprehensive annual performance review",
    blocks: [
      "cover_page",
      "executive_summary",
      "portfolio_overview",
      "performance_attribution",
      "alternatives_detail",
      "holdings_table",
      "fee_summary",
      "disclosures",
    ],
  },
  {
    name: "Capital Activity Update",
    description: "Quick update on capital calls and distributions",
    blocks: ["cover_page", "capital_activity", "fee_summary"],
  },
];
